import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../css/Lp2App.css";
import { apiUrl } from "../config/api";
import { BootCondition, ShoeType, conditionLabel, shoeTypeLabel } from "../api/bootloopClient";

type BootListingCardDto = {
  listingId: number;
  sizeUk: number;
  shoeType: number;
  condition: number;
  notes?: string | null;
  photoUrl?: string | null;
  ownerArea?: string | null;
  createdUtc: string;
  distanceMiles?: number | null;
};

type PageResponse =
  | BootListingCardDto[]
  | {
      items?: BootListingCardDto[];
      nextCursor?: string | null;
      Items?: BootListingCardDto[];
      NextCursor?: string | null;
    };

function normalizePage(res: PageResponse): { items: BootListingCardDto[]; nextCursor: string | null } {
  if (Array.isArray(res)) return { items: res, nextCursor: null };
  const items = (res.items ?? res.Items ?? []) as BootListingCardDto[];
  const nextCursor = (res.nextCursor ?? res.NextCursor ?? null) as string | null;
  return { items, nextCursor };
}

const BootsAvailable: React.FC = () => {
  const location = useLocation();

  const [sizeUk, setSizeUk] = useState<number | "">("");
  const [shoeType, setShoeType] = useState<ShoeType | "">("");
  const [minCondition, setMinCondition] = useState<BootCondition | "">("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [items, setItems] = useState<BootListingCardDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadedOnce, setLoadedOnce] = useState(false);

  // ✅ NEW: highlight card when arriving from create
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const highlightTimer = useRef<number | null>(null);

  const viewerPostcode = (localStorage.getItem("bootloop.postcode") || "").trim();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("new");
    if (!raw) return;

    const id = Number(raw);
    if (!Number.isFinite(id) || id <= 0) return;

    setHighlightId(id);

    if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
    highlightTimer.current = window.setTimeout(() => {
      setHighlightId(null);
      highlightTimer.current = null;
    }, 3000);
  }, [location.search]);

  useEffect(() => {
    return () => {
      if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
    };
  }, []);

  const outward = useMemo(() => {
    if (!viewerPostcode) return "";
    return viewerPostcode.split(/\s+/)[0].toUpperCase();
  }, [viewerPostcode]);

  async function loadFirstPage() {
    setErr(null);

    try {
      setBusy(true);

      const qs = new URLSearchParams();

      if (sizeUk !== "") qs.set("sizeUk", String(sizeUk));
      if (shoeType !== "") qs.set("shoeType", String(shoeType));
      if (minCondition !== "") qs.set("minCondition", String(minCondition));

      qs.set("maxRows", "60");
      if (viewerPostcode) qs.set("viewerPostcode", viewerPostcode);

      const res = await fetch(apiUrl(`/api/listings?${qs.toString()}`));
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed (${res.status}): ${text}`);
      }

      const json = (await res.json()) as PageResponse;
      const page = normalizePage(json);

      setItems(page.items);
      setNextCursor(page.nextCursor);
      setLoadedOnce(true);
    } catch (e: any) {
      setErr(e?.message || "Failed to load listings.");
      setItems([]);
      setNextCursor(null);
      setLoadedOnce(true);
    } finally {
      setBusy(false);
    }
  }

  // initial load
  useEffect(() => {
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="lp2app">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h1 className="lp2app__title">Boots available</h1>
          <p className="lp2app__lead">Browse listings. Filter by size/type/condition.</p>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <Link className="lp2app__btnSecondary" to="/find-swap-v2">
            Find a swap
          </Link>
          <Link className="lp2app__btnSecondary" to="/list-boot">
            List boots
          </Link>
          <Link className="lp2app__btnSecondary" to="/my">
            My stuff
          </Link>
        </div>
      </div>

      {err && <div className="lp2app__alert">{err}</div>}

      {/* Filters */}
      <form
        className="lp2app__card lp2app__form"
        onSubmit={(e) => {
          e.preventDefault();
          loadFirstPage();
        }}
      >
        <div className="lp2app__row3">
          <label className="lp2app__label">
            Size (UK)
            <input
              className="lp2app__input"
              type="number"
              step="0.5"
              min={1}
              max={15}
              value={sizeUk === "" ? "" : sizeUk}
              onChange={(e) => {
                const v = e.target.value;
                setSizeUk(v === "" ? "" : Number(v));
              }}
              placeholder="Any"
            />
          </label>

          <label className="lp2app__label">
            Shoe type
            <select
              className="lp2app__select"
              value={shoeType === "" ? "" : String(shoeType)}
              onChange={(e) =>
                setShoeType(e.target.value === "" ? "" : (Number(e.target.value) as ShoeType))
              }
            >
              <option value="">Any</option>
              <option value="0">{shoeTypeLabel(0)}</option>
              <option value="1">{shoeTypeLabel(1)}</option>
              <option value="2">{shoeTypeLabel(2)}</option>
              <option value="3">{shoeTypeLabel(3)}</option>
              <option value="4">{shoeTypeLabel(4)}</option>
            </select>
          </label>

          <label className="lp2app__label">
            Min condition
            <select
              className="lp2app__select"
              value={minCondition === "" ? "" : String(minCondition)}
              onChange={(e) =>
                setMinCondition(e.target.value === "" ? "" : (Number(e.target.value) as BootCondition))
              }
            >
              <option value="">Any</option>
              <option value="0">{conditionLabel(0)}</option>
              <option value="1">{conditionLabel(1)}</option>
              <option value="2">{conditionLabel(2)}</option>
              <option value="3">{conditionLabel(3)}</option>
              <option value="4">{conditionLabel(4)}</option>
            </select>
          </label>
        </div>

        <div className="lp2app__row2">
          <div className="lp2app__smallMuted">
            {viewerPostcode ? (
              <>
                Distance shown for <strong>{outward}</strong> (from your saved postcode)
              </>
            ) : (
              <>Tip: enter your postcode on “Find a swap” to show distances</>
            )}
          </div>

          <div className="lp2app__actions">
            <button className="lp2app__btnPrimary" type="submit" disabled={busy}>
              {busy ? "Loading…" : "Apply filters"}
            </button>
          </div>
        </div>
      </form>

      {loadedOnce && (
        <div style={{ marginTop: 14, opacity: 0.9 }}>
          {items.length > 0 ? `Showing ${items.length} listings` : "No listings found."}
        </div>
      )}

      {/* Grid */}
      {items.length > 0 && (
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: 16,
            alignItems: "start",
          }}
        >
          {items.map((x) => {
            const isNew = highlightId === x.listingId;

            return (
              <div
                key={x.listingId}
                className={isNew ? "lp2app__cardGlow" : ""}
                style={{
                  borderRadius: 22,
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "rgba(255,255,255,0.04)",
                  boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ height: 140, overflow: "hidden" }}>
                  {x.photoUrl ? (
                    <img
                      src={x.photoUrl}
                      alt={`UK ${x.sizeUk}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                      loading="lazy"
                    />
                  ) : (
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0.7,
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >
                      No photo
                    </div>
                  )}
                </div>

                <div style={{ padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ fontWeight: 900, fontSize: 18 }}>UK {x.sizeUk}</div>

                    <div
                      style={{
                        padding: "4px 10px",
                        borderRadius: 999,
                        border: "1px solid rgba(84,255,170,0.35)",
                        background: "rgba(84,255,170,0.10)",
                        fontSize: 12,
                        fontWeight: 800,
                        whiteSpace: "nowrap",
                        alignSelf: "center",
                      }}
                    >
                      {conditionLabel(Number(x.condition))}
                    </div>
                  </div>

                  <div style={{ marginTop: 6, opacity: 0.85, fontSize: 13 }}>
                    {shoeTypeLabel(Number(x.shoeType))}
                    {x.ownerArea ? ` · ${x.ownerArea}` : ""}
                  </div>

                  <div style={{ marginTop: 6, opacity: 0.85, fontSize: 13 }}>
                    Distance:{" "}
                    {x.distanceMiles != null ? `${Number(x.distanceMiles).toFixed(1)} miles` : "—"}
                  </div>

                  <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
                    <Link
                      to={`/boots/${x.listingId}`}
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        borderRadius: 999,
                        border: "none",
                        color: "#111",
                        fontWeight: 800,
                        textDecoration: "none",
                        textAlign: "center",
                        background: "linear-gradient(90deg,#7b4bff,#ff4bbd)",
                      }}
                    >
                      View
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Paging note (your API paging endpoint differs across builds) */}
      {nextCursor && (
        <div style={{ marginTop: 16, textAlign: "center" }} className="lp2app__smallMuted">
          More results available (cursor paging configured in API).
        </div>
      )}
    </div>
  );
};

export default BootsAvailable;